import React, { useState, useRef, useEffect } from "react";
import { Send, Loader2, FileText, Trash2, RefreshCw, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import ScrollArea from "@/components/ui/shared/ScrollArea";
import type { Database } from "@/lib/supabase";
import { getFriendlyError, type FriendlyError } from "@/lib/friendlyErrors";
import { formatInAccountTz } from "@/lib/accountTimezone";
import { useTranslation } from "react-i18next";

type AccountAssistant = Database['public']['Tables']['account_assistants']['Row']

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  error?: FriendlyError;
}

interface TestLog {
  id: string;
  timestamp: Date;
  assistant_name: string;
  last_message_id_sent: string | null;
  request_url: string;
  request_headers: Record<string, string>;
  request: any;
  response: any;
  http_status: number;
  last_message_id_received: string | null;
}

interface AssistantSimulationChatProps {
  assistant?: AccountAssistant;
}

const AssistantSimulationChat: React.FC<AssistantSimulationChatProps> = ({ assistant }) => {
  const { t } = useTranslation("assistants");
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastMessageId, setLastMessageId] = useState<string | null>(null);
  const [testLogs, setTestLogs] = useState<TestLog[]>([]);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const userText = inputValue;
    setInputValue('');
    setIsLoading(true);

    try {
      const requestTimestamp = new Date();
      const lastMessageIdSent = lastMessageId;

      const payload = {
        last_message_id: lastMessageId,
        text: userText,
        assistant_id: assistant?.id || null
      };

      const requestUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/processamento-ia`;
      const requestHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      };

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify(payload)
      });

      const httpStatus = response.status;

      if (!response.ok) {
        const errorText = await response.text();

        const errorLog: TestLog = {
          id: Date.now().toString(),
          timestamp: requestTimestamp,
          assistant_name: assistant?.name || 'Assistente de Teste',
          last_message_id_sent: lastMessageIdSent,
          request_url: requestUrl,
          request_headers: requestHeaders,
          request: payload,
          response: { error: errorText },
          http_status: httpStatus,
          last_message_id_received: null
        };
        setTestLogs(prev => [errorLog, ...prev]);

        const friendly = getFriendlyError({ raw: errorText, httpStatus });
        const err = new Error(friendly.description) as Error & {
          friendly?: FriendlyError;
        };
        err.friendly = friendly;
        throw err;
      }

      const result = await response.json();

      const successLog: TestLog = {
        id: Date.now().toString(),
        timestamp: requestTimestamp,
        assistant_name: assistant?.name || 'Assistente de Teste',
        last_message_id_sent: lastMessageIdSent,
        request_url: requestUrl,
        request_headers: requestHeaders,
        request: payload,
        response: result,
        http_status: httpStatus,
        last_message_id_received: result.last_message_id || null
      };
      setTestLogs(prev => [successLog, ...prev]);

      if (result.success && result.last_message_id) {
        setLastMessageId(result.last_message_id);
      }

      if (result.success && result.text) {
        // Split response by triple line breaks (\n\n\n) and create multiple messages
        const textLines = result.text.split('\n\n\n').filter((line: string) => line.trim() !== '');

        const botMessages: Message[] = textLines.map((line: string, index: number) => ({
          id: result.last_message_id ? `${result.last_message_id}_${index}` : `${Date.now()}_${index}`,
          text: line.trim(),
          isUser: false,
          timestamp: new Date()
        }));

        setMessages(prev => [...prev, ...botMessages]);
      } else {
        throw new Error(result.error || 'Nenhuma resposta gerada pela IA');
      }

    } catch (error) {
      console.error('Erro ao processar mensagem:', error);

      const friendly: FriendlyError =
        (error as { friendly?: FriendlyError })?.friendly ||
        getFriendlyError({
          errorMessage: (error as Error)?.message || String(error),
        });

      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: friendly.description,
        isUser: false,
        timestamp: new Date(),
        error: friendly,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearLogs = () => {
    if (confirm(t("simulation.confirmClearLogs"))) {
      setTestLogs([]);
    }
  };

  const handleNewConversation = () => {
    if (confirm(t("simulation.confirmNewConversation"))) {
      setMessages([]);
      setLastMessageId(null);
      setTestLogs([]);
      setInputValue('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="h-full flex min-h-0 flex-col overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-sm dark:border-[#3F3F464D] dark:bg-[#20202040] dark:shadow-none">
      <div className="p-4 border-b border-[#E5E7EB] dark:border-[#3F3F464D] flex items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-sm text-[#0F172A] dark:text-[#FAFAFA]">{t("simulation.title")}</h3>
          <p className="text-sm text-[#64748B] dark:text-[#71717A] mt-1">{t("simulation.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleNewConversation}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#2563EB] text-white hover:bg-[#1D4ED8] transition-colors duration-200"
            title={t("simulation.newConversationTitle")}
          >
            <RefreshCw size={16} />
            <span className="text-xs font-medium">{t("simulation.newConversation")}</span>
          </button>
          <Dialog open={isLogModalOpen} onOpenChange={setIsLogModalOpen}>
            <DialogTrigger asChild>
              <button className="relative flex items-center gap-2 px-3 py-2 rounded-xl border border-[#E5E7EB] bg-white text-[#334155] hover:bg-[#F1F5F9] transition-colors duration-200 dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]">
                <FileText size={16} />
                <span className="text-xs font-medium">{t("simulation.logs")}</span>
                {testLogs.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#2563EB] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {testLogs.length}
                  </span>
                )}
              </button>
            </DialogTrigger>
          <DialogContent className="flex !max-w-[900px] rounded-2xl w-full p-0 border border-[#E5E7EB] bg-white text-[#0F172A] flex-col max-h-[90vh] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA]">
            <div className="p-5">
              <DialogHeader className="flex flex-row items-center justify-between">
                <DialogTitle className="text-[18px] font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
                  {t("simulation.testHistory")}
                </DialogTitle>
                <button
                  onClick={handleClearLogs}
                  disabled={testLogs.length === 0}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#DC2626] hover:bg-red-700 disabled:bg-[#E5E7EB] disabled:text-[#94A3B8] disabled:cursor-not-allowed transition-colors duration-200 text-sm text-white dark:disabled:bg-[#3F3F46] dark:disabled:text-[#71717A]"
                >
                  <Trash2 size={14} />
                  {t("simulation.clearLogs")}
                </button>
              </DialogHeader>

              {testLogs.length === 0 ? (
                <div className="mt-6 text-center py-12">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#EEF4FF] dark:bg-blue-500/15">
                    <FileText size={28} className="text-[#2563EB] dark:text-[#60A5FA]" />
                  </div>
                  <p className="text-[#0F172A] dark:text-[#FAFAFA]">{t("simulation.noTests")}</p>
                  <p className="text-[#64748B] dark:text-[#71717A] text-sm mt-2">{t("simulation.noTestsHint")}</p>
                </div>
              ) : (
                <ScrollArea classNames={{ root: "mt-4 max-h-[70vh] overflow-auto" }}>
                  <div className="space-y-3">
                    {testLogs.map((log) => (
                      <div
                        key={log.id}
                        className="border border-[#E5E7EB] rounded-xl p-4 bg-[#F8FAFC] hover:bg-[#F1F5F9] transition-colors duration-200 dark:border-[#3F3F464D] dark:bg-[#18181B] dark:hover:bg-[#202020]"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm text-[#0F172A] dark:text-[#FAFAFA]">{log.assistant_name}</h4>
                            <p className="text-[#64748B] dark:text-[#71717A] text-xs mt-1">
                              {formatInAccountTz(log.timestamp, {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })}
                            </p>
                            <div className="mt-2 space-y-1">
                              <p className="text-[#64748B] dark:text-[#71717A] text-xs">
                                <span className="font-semibold">{t("simulation.sent")}</span>{' '}
                                {log.last_message_id_sent ? (
                                  <span className="font-mono text-[#2563EB] dark:text-[#60A5FA]">{log.last_message_id_sent.substring(0, 20)}...</span>
                                ) : (
                                  <span className="text-[#2563EB] dark:text-[#60A5FA]">Nova conversa</span>
                                )}
                              </p>
                              <p className="text-[#64748B] dark:text-[#71717A] text-xs">
                                <span className="font-semibold">{t("simulation.received")}</span>{' '}
                                {log.last_message_id_received ? (
                                  <span className="font-mono text-[#16A34A] dark:text-[#22C55E]">{log.last_message_id_received.substring(0, 20)}...</span>
                                ) : (
                                  <span className="text-[#DC2626] dark:text-[#F87171]">{t("simulation.none")}</span>
                                )}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              log.http_status === 200
                                ? 'bg-[#ECFDF5] text-[#16A34A] border border-green-200 dark:bg-green-500/10 dark:text-[#22C55E] dark:border-green-500/30'
                                : 'bg-[#FEF2F2] text-[#DC2626] border border-red-200 dark:bg-red-500/10 dark:text-[#F87171] dark:border-red-500/30'
                            }`}
                          >
                            HTTP {log.http_status}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <h5 className="text-[#334155] dark:text-[#D4D4D8] text-xs font-semibold mb-2 flex items-center gap-2">
                              <span className="w-2 h-2 bg-[#2563EB] rounded-full"></span>
                              {t("simulation.request")}
                            </h5>
                            <pre className="bg-white border border-[#E5E7EB] rounded-lg p-3 text-xs text-[#0F172A] overflow-x-auto max-h-[200px] overflow-y-auto dark:bg-[#202020] dark:border-[#3F3F464D] dark:text-[#D4D4D8]">
                              {JSON.stringify(log.request, null, 2)}
                            </pre>
                          </div>
                          <div>
                            <h5 className="text-[#334155] dark:text-[#D4D4D8] text-xs font-semibold mb-2 flex items-center gap-2">
                              <span className="w-2 h-2 bg-[#16A34A] rounded-full"></span>
                              {t("simulation.response")}
                            </h5>
                            <pre className="bg-white border border-[#E5E7EB] rounded-lg p-3 text-xs text-[#0F172A] overflow-x-auto max-h-[200px] overflow-y-auto dark:bg-[#202020] dark:border-[#3F3F464D] dark:text-[#D4D4D8]">
                              {JSON.stringify(log.response, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && !isLoading ? (
          <div className="h-full min-h-[240px] flex flex-col items-center justify-center text-center px-4">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#EEF4FF] dark:bg-blue-500/15">
              <Send size={28} className="text-[#2563EB] dark:text-[#60A5FA]" />
            </div>
            <h4 className="font-semibold text-[#0F172A] dark:text-[#FAFAFA]">{t("simulation.startTitle")}</h4>
            <p className="mt-2 max-w-xs text-sm text-[#64748B] dark:text-[#71717A]">
              {t("simulation.startHint")}
            </p>
          </div>
        ) : null}
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
          >
            {message.error ? (
              <div className="max-w-[85%] rounded-xl border border-red-200 bg-[#FEF2F2] p-3 dark:border-red-500/30 dark:bg-red-500/10">
                <div className="flex items-start gap-2">
                  <AlertTriangle
                    size={18}
                    className="text-[#DC2626] dark:text-[#F87171] mt-0.5 shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#DC2626] dark:text-[#F87171]">
                      {message.error.title}
                    </p>
                    <p className="text-xs text-[#DC2626]/90 dark:text-[#F87171]/90 mt-1 whitespace-pre-wrap break-words">
                      {message.error.description}
                    </p>
                    <span className="text-[10px] text-[#64748B] dark:text-[#71717A] mt-2 block">
                      {formatInAccountTz(message.timestamp, {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={`max-w-[80%] rounded-xl border p-3 ${
                  message.isUser
                    ? 'bg-[#EEF4FF] text-[#0F172A] border-blue-100 dark:bg-blue-500/20 dark:text-[#FAFAFA] dark:border-blue-500/30'
                    : 'bg-white border-[#E5E7EB] text-[#0F172A] dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#FAFAFA]'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
                <span className="text-xs text-[#64748B] dark:text-[#71717A] mt-1 block">
                  {formatInAccountTz(message.timestamp, {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            )}
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] p-3 flex items-center gap-2 dark:border-[#3F3F46] dark:bg-[#27272A]">
              <Loader2 className="w-4 h-4 animate-spin text-[#64748B] dark:text-[#71717A]" />
              <span className="text-sm text-[#64748B] dark:text-[#71717A]">{t("simulation.processing")}</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-4 border-t border-[#E5E7EB] dark:border-[#3F3F464D]">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t("simulation.inputPlaceholder")}
            className="flex-1 h-11 rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#0F172A] placeholder:text-[#94A3B8] outline-none focus-visible:border-[#2563EB] focus-visible:ring-[#2563EB]/20 focus-visible:ring-[3px] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]"
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading}
            aria-label={t("simulation.sendMessage")}
            className="h-11 rounded-xl border border-[#E5E7EB] px-4 text-[#2563EB] hover:bg-[#EEF4FF] transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50 dark:border-[#3F3F46] dark:text-[#60A5FA] dark:hover:bg-blue-500/15"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssistantSimulationChat;
